import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth-context";
import { useCountry, COUNTRIES } from "@/lib/country-context";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Utensils } from "lucide-react";
import api from "@/lib/api";

export default function SigninPage() {
  const { signinCustomer } = useAuth();
  const { country, setCountry, locked } = useCountry();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const [phoneLocal, setPhoneLocal] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const from = location.state?.from || "/";
  const dialCode = country.dialCode;

  const setLocal = (raw) => setPhoneLocal((raw || "").replace(/\D/g, ""));

  // Best-effort, silent country auto-detect from the browser's location —
  // never blocks the form, never shows an error, and never overrides a
  // returning user's already-locked (registered) country.
  useEffect(() => {
    if (locked || !("geolocation" in navigator)) return;
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const { data } = await api.get("/geocode/reverse", {
            params: { lat: pos.coords.latitude, lon: pos.coords.longitude },
          });
          if (data.country_code === "GH" || data.country_code === "NG") {
            setCountry(data.country_code);
          }
        } catch {
          // silent — dropdown just keeps its current default
        }
      },
      () => {}, // silent — no permission, no problem
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 300000 }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locked]);

  const submit = async (e) => {
    e.preventDefault();
    if (phoneLocal.length < 6) {
      toast({ title: "Enter a valid phone number", variant: "destructive" });
      return;
    }
    const fullPhone = `${dialCode}${phoneLocal.replace(/^0+/, "")}`;
    setLoading(true);
    try {
      await signinCustomer(fullPhone, password);
      toast({ title: "Welcome back!" });
      navigate(from, { replace: true });
    } catch (err) {
      toast({
        title: "Sign in failed",
        description: err?.response?.data?.detail || "Check your credentials",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6 py-10">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-6" data-testid="signin-logo">
            <div className="w-12 h-12 rounded-2xl bg-primary text-primary-foreground flex items-center justify-center">
              <Utensils className="w-6 h-6" />
            </div>
          </Link>
          <h1 className="font-serif text-3xl font-bold">Welcome back</h1>
          <p className="text-muted-foreground mt-2">Sign in to start craving.</p>
        </div>

        <form onSubmit={submit} className="bg-card border rounded-2xl p-6 space-y-4 shadow-sm">
          <div className="space-y-2">
            <Label htmlFor="phone">Phone number</Label>
            <div className="flex items-stretch h-10 rounded-md border border-input bg-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 overflow-hidden">
              {locked ? (
                <div
                  className="flex items-center gap-1 px-3 bg-muted text-muted-foreground text-sm font-semibold select-none border-r"
                  data-testid="signin-dial-code"
                >
                  <span className="text-base leading-none">{country.flag}</span>
                  <span>{dialCode}</span>
                </div>
              ) : (
                <select
                  value={country.code}
                  onChange={(e) => setCountry(e.target.value)}
                  className="bg-muted text-muted-foreground text-sm font-semibold border-r pl-2 pr-1 focus:outline-none cursor-pointer"
                  data-testid="signin-dial-code-select"
                  aria-label="Country code"
                >
                  {["GH", "NG"].map((code) => (
                    <option key={code} value={code}>
                      {COUNTRIES[code].flag} {COUNTRIES[code].dialCode}
                    </option>
                  ))}
                </select>
              )}
              <input
                id="phone"
                type="tel"
                inputMode="numeric"
                required
                value={phoneLocal}
                onChange={(e) => setLocal(e.target.value)}
                placeholder={country.code === "GH" ? "24 123 4567" : "801 234 5678"}
                className="flex-1 bg-transparent px-3 text-sm placeholder:text-muted-foreground focus:outline-none"
                data-testid="signin-phone-input"
                autoComplete="tel-national"
              />
            </div>
            {!locked && (
              <p className="text-xs text-muted-foreground">
                Pick your country code, then enter your local number.
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <Input id="password" type="password" autoComplete="current-password" required value={password}
              onChange={(e) => setPassword(e.target.value)} placeholder="••••••••"
              data-testid="signin-password-input" />
          </div>
          <Button type="submit" className="w-full h-11 font-semibold" disabled={loading} data-testid="signin-submit-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign in"}
          </Button>
        </form>

        <p className="text-center mt-6 text-sm text-muted-foreground">
          New to Cravve?{" "}
          <Link to="/signup" className="text-primary font-semibold hover:underline" data-testid="signin-signup-link">
            Create an account
          </Link>
        </p>
        <p className="text-center mt-3 text-xs text-muted-foreground">
          Restaurant owner?{" "}
          <Link to="/restaurant-portal/signin" className="text-primary hover:underline" data-testid="signin-restaurant-link">
            Restaurant sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
